import { NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { ContactDetailsComponent } from '../../../../content/contact-details/contact-details.component';
import {
  ImageGalleryComponent,
  type GalleryImage,
} from '../../../../content/image-gallery/image-gallery.component';
import { YoutubeSectionComponent } from '../../../../content/youtube-section/youtube-section.component';
import { SEOService } from '../../../../seo.service';
import { buildPerformancePageSchemas } from './performance-jsonld';

@Component({
  selector: 'app-performance-content',
  imports: [
    NgOptimizedImage,
    RouterLink,
    YoutubeSectionComponent,
    ContactDetailsComponent,
    ImageGalleryComponent,
  ],
  templateUrl: './performance-content.component.html',
  styleUrl: './performance-content.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PerformanceContentComponent implements OnDestroy {
  private seo = inject(SEOService);

  constructor() {
    // No route param to react to, so a plain constructor call is enough —
    // this page's JSON-LD never changes after mount. Sets the VideoObject
    // schemas that used to live in app.routes.ts's static data.seo.jsonLd;
    // see performance-jsonld.ts for why this moved.
    this.seo.setRouteSchemas(buildPerformancePageSchemas());
  }

  ngOnDestroy(): void {
    this.seo.clearRouteSchemas();
  }

  // Full-color, landscape performance gallery: Russ out playing rooms and
  // festivals, solo and with bands. Distinct from the bio gallery's square,
  // portrait-leaning set.
  protected readonly galleryImages: GalleryImage[] = [
    // gig-warm stays in bio-gallery/ — Groove styleguide + image-gallery spec also reference it (#65).
    {
      src: '/images/bio-gallery/gig-warm.webp',
      alt: 'Russ Rodgers playing bass at a club gig under warm red lighting',
      aspectRatio: '3 / 2',
      sizes: '(max-width: 600px) 90vw, (max-width: 900px) 45vw, 30vw',
    },
    {
      src: '/images/performance-gallery/full-band.webp',
      alt: 'Russ Rodgers performing on stage with a full band',
      aspectRatio: '3 / 2',
      sizes: '(max-width: 600px) 90vw, (max-width: 900px) 45vw, 30vw',
    },
    {
      src: '/images/performance-gallery/band-sax.webp',
      alt: 'Russ Rodgers playing bass in a band with a saxophone player',
      aspectRatio: '3 / 2',
      sizes: '(max-width: 600px) 90vw, (max-width: 900px) 45vw, 30vw',
    },
    {
      src: '/images/performance-gallery/outdoor-set.webp',
      alt: 'Russ Rodgers playing bass at an outdoor performance',
      aspectRatio: '3 / 2',
      sizes: '(max-width: 600px) 90vw, (max-width: 900px) 45vw, 30vw',
    },
    {
      src: '/images/performance-gallery/with-vocalist.webp',
      alt: 'Russ Rodgers backing a vocalist on bass during a live set',
      aspectRatio: '3 / 2',
      sizes: '(max-width: 600px) 90vw, (max-width: 900px) 45vw, 30vw',
    },
    {
      src: '/images/performance-gallery/blues-festival.webp',
      alt: 'Russ Rodgers performing at an outdoor blues festival',
      aspectRatio: '3 / 2',
      sizes: '(max-width: 600px) 90vw, (max-width: 900px) 45vw, 30vw',
    },
    {
      src: '/images/performance-gallery/band-color.webp',
      alt: 'Russ Rodgers on bass with his band under colorful stage lighting',
      aspectRatio: '3 / 2',
      sizes: '(max-width: 600px) 90vw, (max-width: 900px) 45vw, 30vw',
    },
    {
      src: '/images/performance-gallery/duo-hat.webp',
      alt: 'Russ Rodgers playing bass beside a vocalist in a hat',
      aspectRatio: '3 / 2',
      sizes: '(max-width: 600px) 90vw, (max-width: 900px) 45vw, 30vw',
    },
    {
      src: '/images/performance-gallery/outdoor-festival.webp',
      alt: 'Russ Rodgers playing bass with a band at an outdoor festival',
      aspectRatio: '3 / 2',
      sizes: '(max-width: 600px) 90vw, (max-width: 900px) 45vw, 30vw',
    },
    {
      src: '/images/performance-gallery/jazz-portrait.webp',
      alt: 'Russ Rodgers in a dark suit playing bass on stage',
      aspectRatio: '3 / 2',
      sizes: '(max-width: 600px) 90vw, (max-width: 900px) 45vw, 30vw',
    },
    // 2026-07/08 email deliveries — the first Florida-era shots (Guanabanas
    // in Jupiter, plus two more; roadmap #86 relocation relevance).
    {
      src: '/images/performance-gallery/guanabanas-band.webp',
      alt: 'Russ Rodgers on bass with a blues quartet at Guanabanas in Jupiter, Florida',
      aspectRatio: '3 / 2',
      sizes: '(max-width: 600px) 90vw, (max-width: 900px) 45vw, 30vw',
    },
    {
      src: '/images/performance-gallery/guanabanas-seated.webp',
      alt: 'Russ Rodgers seated with his six-string bass in front of the Guanabanas mural',
      aspectRatio: '3 / 2',
      sizes: '(max-width: 600px) 90vw, (max-width: 900px) 45vw, 30vw',
      // Tall portrait; keep the face in the 3:2 cover crop.
      position: '50% 18%',
    },
    {
      src: '/images/performance-gallery/restaurant-jazz.webp',
      alt: 'Russ Rodgers standing with his six-string bass at a restaurant jazz gig with a pianist',
      aspectRatio: '3 / 2',
      sizes: '(max-width: 600px) 90vw, (max-width: 900px) 45vw, 30vw',
      position: '50% 25%',
    },
    {
      src: '/images/performance-gallery/tapestry-room.webp',
      alt: 'Five-piece band with vocalist and saxophone in an ornate tapestry-walled room, Russ Rodgers on bass',
      aspectRatio: '3 / 2',
      sizes: '(max-width: 600px) 90vw, (max-width: 900px) 45vw, 30vw',
    },
  ];
}
